/* eslint-disable no-secrets/no-secrets */
import React, { useContext, useCallback, useEffect } from 'react';
import _ from 'lodash';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { BreakpointTrackerContext } from '@jutro/layout';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { UWIssuesUtil } from "premier-policycommon-util-js";
import VehicleCoverage from "gw-capability-quoteandbind-PremierRV-react/components/VehicleCoverage/VehicleCoverage"
import { DatatableUtil } from '@xengage/gw-portals-util-js';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import PrintDocumentsComponent from "premier-capability-policyjob-react/components/PrintDocumentsComponent/PrintDocumentsComponent";
import FormsComponent from "premier-capability-policyjob-react/components/FormComponent/FormComponent";
import { PremierConstants } from 'premier-constant-service';
import QuoteValidityComponent from "premier-capability-policyjob-react/components/QuoteValidityComponent/QuoteValidityComponent";
import QuoteDetailsComponent from 'premier-capability-policyjob-react/components/QuoteDetailsComponent/QuoteDetailsComponent';
import styles from './QuotePage.module.scss';
import presentationMetadata from './QuotePage.metadata.json5';
import messages from './QuotePage.messages';
import CoveragesPageComponent from "../Coverages/CoveragesPage";
import InternalUseComponent from "../../components/subcomponents/InternalUse/InternalUseComponent"

function QuotePage(props) {
    const {
        wizardData: submissionVM,
        steps,
        jumpTo,
        currentStep,
        wizardData, updateWizardData,
        underwritingIssues
    } = props;

    const { authHeader } = useAuthentication();
    const breakpoint = useContext(BreakpointTrackerContext);
    const { pageKey } = currentStep;
    const isQuoteBlocked = UWIssuesUtil.isQuoteBlocked(underwritingIssues);
    const findBlockingPoint = UWIssuesUtil.findBlockingPoint(underwritingIssues);
    const underWritingIssuesNonBlocking = UWIssuesUtil.UWIssuesNonBlocking(underwritingIssues);
    const translator = useTranslator();
    const { lineName } = PremierConstants;
    const productCode = _.get(submissionVM.value, 'baseData.productCode');
    // eslint-disable-next-line security/detect-object-injection
    const lobLineName = productCode ? lineName[productCode] : '';

    // eslint-disable-next-line security/detect-object-injection
    const metadata = presentationMetadata[pageKey];

    const handleNext = useCallback(
        () => {
            const offeredQuotes = _.get(submissionVM.value, 'quoteData.offeredQuotes[0].publicID');

            _.set(submissionVM, 'bindData.chosenQuote', offeredQuotes);

            return submissionVM;
        },
        [submissionVM]
    );

    const navigateToCoverages = useCallback(
        () => {
            const selectOffering = submissionVM.value.lobData.proLine.selectedOffering_Ext
            let redirectPath = ''

            switch (selectOffering) {
                case 'ArcEng':
                    redirectPath = '/ArchitectEngineer';
                    break;
                case 'MisPro':
                    redirectPath = '/MiscellaneousProfessionals';
                    break;
                case 'ITPro':
                    redirectPath = '/ITProfessional';
                    break;
                case 'Acc':
                    redirectPath = '/accountantprofessionals';
                    break;
                case 'LifAge':
                    redirectPath = '/LifeAgentProfessionals';
                    break;
                case 'DirAndOff':
                    redirectPath = '/DirectorAndOfficerProfessionals';
                    break;
                case 'Med':
                    redirectPath = '/MedicalPractitionersProfessionals';
                    break;
                default:
                    break;
            }

            const indexOfGeneralCoverages = _.findIndex(
                steps,
                ({ path }) => path === redirectPath
            );

            jumpTo(indexOfGeneralCoverages);
        },
        [jumpTo, steps, submissionVM]
    );

    // eslint-disable-next-line arrow-body-style
    const getCell = (item, _index, property) => {
        return item[property.id];
    };

    const getFormTableData = () => _.map(submissionVM.value.lobData.proLine.lineCoverages.forms_Ext, (form) => ({
        formDes: form.formDes,
        formCategory: form.formCategory
    }));

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            navigateToCoverages,
            getCell,
            sortString: DatatableUtil.sortString,
        },
        resolveComponentMap: {
            vehiclecoverage: VehicleCoverage,
            coveragespagecomponent: CoveragesPageComponent,
            internalUseComponent: InternalUseComponent,
            quoteDetailsComponent: QuoteDetailsComponent,
            quoteValidityComponent: QuoteValidityComponent,
            PrintDocumentsComponent,
            FormsComponent
        }
    };

    const generatelocRiskseOverrides = () => {

        const overrides = submissionVM.value.lobData.proLine.coverables.locRisks.map((locRisk, index) => ({
            [`locRiskSection${index}`]: {
                title: locRisk.accountAddressOfLocRisk.displayName
            },
            [`locRiskCoverageInfoComponent${index}`]: {
                wizardData,
                updateWizardData,
                clausesBasePath: `lobData.proLine.lineCoverages.locRiskCoverages.coverages.children[${index}].coverages`,
                title: messages.covAccordion,
                clasueType: 'coverages',
                isEditable: false
            }
        }));

        return Object.assign({}, ...overrides);
    };

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        underWritingIssues: {
            underwritingIssues: underWritingIssuesNonBlocking,
            blockQuote: findBlockingPoint,
            filterUWIssuesInCustomOffering: false,
            quoteID: _.get(submissionVM.value, 'quoteID'),
            accountNumber: _.get(submissionVM.value, 'baseData.accountNumber')
        },
        liabilityCoverageInfoComponent: {
            wizardData,
            updateWizardData,
            clausesBasePath: `lobData.proLine.lineCoverages.coverages`,
            title: messages.covAccordion,
            clasueType: 'coverages',
            isEditable: false
        },
        quoteDetailsComponent: {
            wizardData
        },
        quoteValidityComponent: {
            wizardData
        },
        subConQuo4: {
            visible: _.get(submissionVM, 'lobData.proLine.selectedOffering_Ext.value') === 'DirAndOff'
        },
        addReq2: {
            visible: _.get(submissionVM, 'lobData.proLine.selectedOffering_Ext.value') === 'DirAndOff'
        },
        additionalQuoteComponent: {
            visible: _.get(submissionVM.value, 'lobData.proLine.coverables.internalUse.additionalConditions')?.length > 0,
            data: _.get(submissionVM, 'lobData.proLine.coverables.internalUse') ? _.get(submissionVM, 'lobData.proLine.coverables.internalUse.additionalConditions') : [],
            isSubjectQuote: false
        },
        subjectQuoteComponent: {
            visible: _.get(submissionVM.value, 'lobData.proLine.coverables.internalUse.subjectConditions')?.length > 0,
            data: _.get(submissionVM, 'lobData.proLine.coverables.internalUse') ? _.get(submissionVM, 'lobData.proLine.coverables.internalUse.subjectConditions') : [],
            isSubjectQuote: true
        },
        tableFormSummary: {
            data: getFormTableData()
        },
        policyQuoteValDaysHead: {
            content: translator(messages.plQuoteValidityDays, {
                quoteValidityDays: _.get(submissionVM, `baseData.quoteValidityDays_Ext.value`)
            })
        },
        taxes: {
            visible: _.get(submissionVM.value, 'baseData.baseState_Ext') === 'QC',
        },
        fees: {
            visible: _.get(submissionVM.value, 'quoteData.offeredQuotes') &&
                _.get(submissionVM.value, 'quoteData.offeredQuotes[0].premium.fees.amount') !== 0
        },
        policyfees: {
            visible: _.get(submissionVM.value, 'baseData.policyFee_Ext.amount') !== 0
        },
        inspectionFee: {
            visible: _.get(submissionVM.value, 'baseData.inspectionFee_Ext.amount') !== 0
        },
        extensionFee: {
            visible: _.get(submissionVM.value, 'baseData.extensionFee_Ext.amount') !== 0
        },
        PrintDocumentsComponent: {
            visible: submissionVM.value.baseData.periodStatus === 'Quoted',
            jobNumber: submissionVM.value.quoteID,
            offeringName: _.get(submissionVM.value, `lobData.${lobLineName}.selectedOffering_Ext`),
            authHeader
        },
        FormsComponent: {
            forms: submissionVM.value.lobData.proLine.lineCoverages.forms_Ext
        },
        QuoteLocRiskeContainer: {
            visible: _.get(submissionVM.value, `lobData.${lobLineName}.selectedOffering_Ext`) !== 'LifAge'
        },
        ...generatelocRiskseOverrides()
    };

    useEffect(() => { }, []);

    return (
        <WizardPage
            disableNext={underWritingIssuesNonBlocking?.length > 0}
            onNext={handleNext}
        >
            <ViewModelForm
                uiProps={metadata.content}
                model={submissionVM}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

QuotePage.propTypes = wizardProps;
export default QuotePage;
